import {
  AccordionElement,
  Box,
  Button,
  ButtonGroup,
  Direction,
  Flex,
  FormControlSizes,
  IconButton,
  IconName,
  IndicatorBadge,
  Input,
  MixpanelEvent,
  MixpanelEventProperty,
  Stack,
  Text,
  useConnectionStatusContext,
  useMixpanel,
  useObservableValue,
} from '@talos/kyoko';
import { useCallback, useMemo, useState } from 'react';
import { map } from 'rxjs';
import { type SortMode, isConnConfiguredAndNotOffline } from '../../utils/status';
import { useNumConnected } from '../NavigationSidebar/hooks/useNumConnected';
import { SORT_MODE_LABELS, StatusTable } from './StatusTable';

/**
 * Table suitable for use in a tooltip, showing the status of all market credentials
 */
export function GlobalMarketStatusTable() {
  const mixpanel = useMixpanel();
  const [searchValue, setSearchValue] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [sortMode, setSortMode] = useState<SortMode>('Status');
  const [sortDirection, setSortDirection] = useState<Direction>(Direction.Asc);
  const { marketCredentialsWithConnectionStatusByCredentialID } = useConnectionStatusContext();
  const marketCredentialsWithStatus = useObservableValue(
    () =>
      marketCredentialsWithConnectionStatusByCredentialID.pipe(
        map(connsWithStatus => [...connsWithStatus.values()].filter(isConnConfiguredAndNotOffline))
      ),
    [marketCredentialsWithConnectionStatusByCredentialID],
    []
  );
  const filteredMarketCredentialsWithStatus = useMemo(() => {
    return marketCredentialsWithStatus.filter(cred =>
      searchValue !== '' ? cred.credential.Label.toLowerCase().includes(searchValue.toLowerCase()) : true
    );
  }, [marketCredentialsWithStatus, searchValue]);

  const numConnected = useNumConnected();

  const handleSortModeClick = useCallback(() => {
    setSortMode(prev => {
      // Cycle through: Status -> Market -> A-Z -> Status
      const sortModeOrder: SortMode[] = ['Status', 'Market', 'Alphabetical'];
      const currentIndex = sortModeOrder.indexOf(prev);
      const newSortMode = sortModeOrder[(currentIndex + 1) % sortModeOrder.length];
      mixpanel.track(MixpanelEvent.ToggleMarketCredentialsSortMode, { [MixpanelEventProperty.SortMode]: newSortMode });
      return newSortMode;
    });
  }, [mixpanel]);

  const handleSortDirectionClick = useCallback(() => {
    setSortDirection(prev => {
      const newSortDirection = prev === Direction.Asc ? Direction.Desc : Direction.Asc;
      mixpanel.track(MixpanelEvent.ToggleMarketCredentialsSortDirection, {
        [MixpanelEventProperty.SortDirection]: newSortDirection,
      });
      return newSortDirection;
    });
  }, [mixpanel]);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  }, []);

  const handleToggleSearch = useCallback(() => {
    setSearchValue('');
    setIsSearchExpanded(prev => {
      const newExpanded = !prev;
      mixpanel.track(MixpanelEvent.ToggleMarketCredentialsSearch, { [MixpanelEventProperty.Expanded]: newExpanded });
      return newExpanded;
    });
  }, [mixpanel]);

  return (
    <Stack position="relative" overflow="hidden" data-testid="global-market-status-table">
      <Flex
        px="spacing12"
        py="spacing4"
        gap="spacing4"
        alignItems="center"
        background="backgroundDropdownGroupHeader"
        position="sticky"
        top="0"
        zIndex="1"
      >
        <Text>Connections</Text>
        <IndicatorBadge data-testid="connections-badge" color="gray.100">
          {numConnected}
        </IndicatorBadge>
        <Flex gap="spacing4" ml="auto">
          <IconButton
            data-testid="search-toggle-button"
            size={FormControlSizes.Tiny}
            icon={IconName.Search}
            onClick={handleToggleSearch}
            color={isSearchExpanded ? 'gray.100' : 'gray.080'}
          />
          <ButtonGroup size={FormControlSizes.Tiny}>
            <IconButton
              data-testid="sort-direction-button"
              size={FormControlSizes.Tiny}
              icon={sortDirection === Direction.Asc ? IconName.SortAscending : IconName.SortDescending}
              onClick={handleSortDirectionClick}
            />
            <Button data-testid="sort-mode-button" size={FormControlSizes.Tiny} onClick={handleSortModeClick}>
              {SORT_MODE_LABELS[sortMode]}
            </Button>
          </ButtonGroup>
        </Flex>
      </Flex>
      <AccordionElement isOpen={isSearchExpanded}>
        <Input
          data-testid="search-input"
          value={searchValue}
          onChange={handleSearchChange}
          embedded
          clearable
          autoFocus
        />
      </AccordionElement>
      <Box flex="1 1 auto" overflow="auto">
        <StatusTable
          marketCredentialsWithStatus={filteredMarketCredentialsWithStatus}
          sortMode={sortMode}
          sortDirection={sortDirection}
        />
      </Box>
    </Stack>
  );
}
